/* V166.3 — Diagnostic Funnel Admin Tab
 *
 * Displays scans, conversion rate, MRR estimate, bundle distribution, and a
 * recent scans table. Authenticated via X-Admin-Token (same pattern as
 * Launch Checklist / Ad Creative Studio tabs).
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BUNDLE_LABELS = {
  visibility: "Viditeľnosť (SEO/GEO)",
  conversion: "Konverzia (košík/dôvera)",
  ops: "Prevádzka (admin/GDPR)",
};

function StatCard({ label, value, sub, color = "text-emerald-300" }) {
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4">
      <div className="text-[10px] text-zinc-300 font-mono uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-2xl font-black ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminDiagnosticFunnelTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) {
      setErr("Admin token chýba. Prihlás sa znova.");
      return;
    }
    setLoading(true);
    setErr("");
    axios
      .get(`${API}/diagnostic-funnel/admin/overview`, { headers: { "X-Admin-Token": token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div className="p-6 text-zinc-500" data-testid="diag-admin-loading">Načítavam diagnostický funnel…</div>;
  }
  if (err) {
    return (
      <div className="p-6">
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-4 text-sm rounded" data-testid="diag-admin-error">
          {err}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const totals = data.totals || {};
  const bundles = data.bundle_breakdown || [];
  const recent = data.recent_scans || [];
  const maxBundle = Math.max(1, ...bundles.map((b) => b.count));

  return (
    <div className="p-3 sm:p-6 space-y-6" data-testid="admin-diagnostic-funnel-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white">📊 Diagnostika Funnel</h2>
          <p className="text-zinc-400 text-sm mt-1">V166.2 — &quot;Máš e-shop, nemáš objednávky?&quot; · /diagnostika-eshopu</p>
        </div>
        <button
          data-testid="diag-admin-refresh"
          onClick={load}
          className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs rounded hover:bg-zinc-800"
        >
          ↻ Obnoviť
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="diag-admin-kpis">
        <StatCard label="Skeny celkom" value={(totals.total_scans || 0).toLocaleString("sk-SK")} color="text-white"/>
        <StatCard label="Dnes" value={totals.scans_today || 0} color="text-emerald-300"/>
        <StatCard label="Checkouty" value={totals.total_checkouts || 0} color="text-amber-300"/>
        <StatCard label="Conversion" value={`${totals.conversion_rate_pct || 0}%`} sub="scan → checkout" color="text-violet-300"/>
        <StatCard label="Promo MRR" value={`€${(totals.promo_mrr_eur || 0).toLocaleString("sk-SK")}`} sub="prvé 3 mes (−50%)" color="text-emerald-400"/>
        <StatCard label="Plný MRR (po promo)" value={`€${(totals.projected_full_mrr_eur || 0).toLocaleString("sk-SK")}`} sub="po skončení zľavy" color="text-emerald-200"/>
        <StatCard label="Exit-intent leady" value={(totals.total_exit_captures || 0).toLocaleString("sk-SK")} sub="zachytené pred odchodom" color="text-rose-300"/>
        <StatCard label="Exit dnes" value={totals.exit_captures_today || 0} sub="cez magic link drip" color="text-rose-200"/>
      </div>

      {/* Bundle distribution */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4 sm:p-5">
        <h3 className="text-sm font-bold text-zinc-300 mb-3">Distribúcia odporúčaných bundlov</h3>
        {bundles.length === 0 && <div className="text-zinc-500 text-sm">Zatiaľ žiadne skeny.</div>}
        <div className="space-y-2" data-testid="diag-admin-bundles">
          {bundles.map((b) => (
            <div key={b.bundle_id} className="flex items-center gap-3" data-testid={`bundle-row-${b.bundle_id}`}>
              <div className="w-44 text-xs text-zinc-300 shrink-0">{BUNDLE_LABELS[b.bundle_id] || b.bundle_id}</div>
              <div className="flex-1 h-6 bg-zinc-900 rounded overflow-hidden">
                <div
                  className="h-full bg-emerald-500/70"
                  style={{ width: `${(b.count / maxBundle) * 100}%` }}
                />
              </div>
              <div className="w-12 text-right text-xs font-mono text-zinc-400">{b.count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent scans table */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl overflow-hidden">
        <h3 className="text-sm font-bold text-zinc-300 px-4 sm:px-5 py-3 border-b border-zinc-800">
          Posledné skeny ({recent.length})
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="diag-admin-table">
            <thead className="bg-zinc-900 text-zinc-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-mono">Kedy</th>
                <th className="text-left px-4 py-2 font-mono">URL</th>
                <th className="text-left px-4 py-2 font-mono">Email</th>
                <th className="text-left px-4 py-2 font-mono">Bundle</th>
                <th className="text-right px-4 py-2 font-mono">V</th>
                <th className="text-right px-4 py-2 font-mono">C</th>
                <th className="text-right px-4 py-2 font-mono">O</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan={7} className="text-center text-zinc-500 py-6">Žiadne skeny zatiaľ.</td></tr>
              )}
              {recent.map((s, idx) => (
                <tr key={idx} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                  <td className="px-4 py-2 text-zinc-500 font-mono whitespace-nowrap">
                    {(s.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-4 py-2 text-zinc-300 max-w-xs truncate">
                    <a href={s.url} target="_blank" rel="noreferrer" className="hover:text-emerald-300">{s.url}</a>
                  </td>
                  <td className="px-4 py-2 text-zinc-400">{s.email || "—"}</td>
                  <td className="px-4 py-2 text-emerald-300 font-bold">{BUNDLE_LABELS[s.prescribed_bundle] || s.prescribed_bundle || "—"}</td>
                  <td className="px-4 py-2 text-right text-zinc-400 font-mono">{s.scores?.visibility ?? "—"}</td>
                  <td className="px-4 py-2 text-right text-zinc-400 font-mono">{s.scores?.conversion ?? "—"}</td>
                  <td className="px-4 py-2 text-right text-zinc-400 font-mono">{s.scores?.ops ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
